import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

interface Enhanced3DBarChartProps {
  data: any[];
  dataKeys: { key: string; name: string; color: string }[];
  xAxisKey: string;
  title?: string;
  height?: number;
}

const Enhanced3DBarChart: React.FC<Enhanced3DBarChartProps> = ({
  data,
  dataKeys,
  xAxisKey,
  title,
  height = 320,
}) => {
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-4 border border-gray-200"
        >
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 mb-1">
              <div
                className="w-3 h-3 rounded-full"
                style={{ background: entry.color }}
              />
              <span className="text-sm text-gray-600">{entry.name}:</span>
              <span className="text-sm font-semibold text-gray-900">{entry.value}</span>
            </div>
          ))}
        </motion.div>
      );
    }
    return null;
  };

  // Custom bar shape with 3D effect
  const Custom3DBar = (props: any) => {
    const { fill, x, y, width, height } = props;
    
    return (
      <g>
        {/* Main bar */}
        <motion.rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          initial={{ height: 0, y: y + height }}
          animate={{ height, y }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
        
        {/* Top face (3D effect) */}
        <motion.path
          d={`M ${x} ${y} L ${x + width / 2} ${y - 4} L ${x + width} ${y} L ${x + width} ${y} Z`}
          fill={fill}
          opacity={0.8}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        />
        
        {/* Right face (3D effect) */}
        <motion.path
          d={`M ${x + width} ${y} L ${x + width + 4} ${y + 4} L ${x + width + 4} ${y + height + 4} L ${x + width} ${y + height} Z`}
          fill={fill}
          opacity={0.6}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        />
        
        {/* Highlight */}
        <motion.rect
          x={x + 2}
          y={y + 2}
          width={width / 3}
          height={height - 4}
          fill="white"
          opacity={0.2}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        />
      </g>
    );
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <defs>
            {dataKeys.map((dataKey, index) => (
              <linearGradient key={index} id={`gradient-${dataKey.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={dataKey.color} stopOpacity={1} />
                <stop offset="100%" stopColor={dataKey.color} stopOpacity={0.7} />
              </linearGradient>
            ))}
          </defs>
          
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
            opacity={0.5}
          />
          
          <XAxis
            dataKey={xAxisKey}
            stroke="#9ca3af"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            dy={10}
          />
          
          <YAxis
            stroke="#9ca3af"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#6b7280' }}
          />
          
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f3f4f6', opacity: 0.3 }} />
          
          <Legend
            iconType="circle"
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
          />
          
          {dataKeys.map((dataKey, index) => (
            <Bar
              key={index}
              dataKey={dataKey.key}
              fill={`url(#gradient-${dataKey.key})`}
              name={dataKey.name}
              radius={[8, 8, 0, 0]}
              barSize={32}
              shape={<Custom3DBar />}
            >
              {data.map((entry, idx) => (
                <Cell key={`cell-${idx}`} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Enhanced3DBarChart;

